import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faLock, faSpinner, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { createPaymentIntent } from '../api/stripe';
import './DashboardUnlock.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51SHEVP2Y8TDovOpusSSoLWA65y4dwsrWFQDYB0mHbdwepJS94KGyDPnKrXjnOlZTtbfqo0Q8Kv0KOEMxuDQ0KGIv00NjBtWwf0');

const UnlockForm = ({ estimatedSavings, onSuccess, onCancel, username }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [succeeded, setSucceeded] = useState(false);

  // Flat subscription fee
  const savingsAmount = Number.isFinite(estimatedSavings) ? estimatedSavings : 0;
  const unlockFee = 129;
  const unlockFeeCents = unlockFee * 100; // Convert to cents for Stripe
  const netSavings = Math.max(savingsAmount - unlockFee, 0);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    try {
      // Create payment intent using session-based endpoint
      const metadata = {
        purpose: 'dashboard-unlock',
      };
      if (username) {
        metadata.username = username;
      }

      const { clientSecret } = await createPaymentIntent(unlockFeeCents, 'usd');

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: 'Dashboard Unlock',
          },
        }
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
      } else if (paymentIntent.status === 'succeeded') {
        setSucceeded(true);
        setLoading(false);
        
        if (onSuccess) {
          onSuccess(paymentIntent, unlockFee, estimatedSavings);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An error occurred while processing your payment. Please try again.');
      setLoading(false);
    }
  };

  if (succeeded) {
    return (
      <div className="unlock-success">
        <div className="success-icon">
          <FontAwesomeIcon icon={faLock} />
        </div>
        <h3>Payment Successful!</h3>
        <p>Your payment has been processed successfully. Your dashboard access is being updated...</p>
        <div style={{ marginTop: '20px' }}>
          <button onClick={onCancel} className="btn-primary" style={{ marginRight: '10px' }}>
            Check Dashboard
          </button>
          <button onClick={() => window.location.reload()} className="btn-secondary">
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="unlock-form">
      <div className="savings-summary">
        <h3>Unlock Your Tax Optimization Details</h3>
        <div className="savings-breakdown">
          <div className="savings-amount">
            <span className="label">Estimated Tax Savings:</span>
            <span className="amount">${savingsAmount.toLocaleString()}</span>
          </div>
          <div className="unlock-fee">
            <span className="label">Unlock Fee (Flat):</span>
            <span className="amount">${unlockFee}</span>
          </div>
          <div className="net-savings">
            <span className="label">Your Net Savings (after fee):</span>
            <span className="amount">${netSavings.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="payment-section">
        <h4>Payment Information</h4>
        <div className="card-element-container">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}

      <div className="payment-actions">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={!stripe || loading}
        >
          {loading ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin />
              Processing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCreditCard} />
              Pay ${unlockFee} to Unlock
            </>
          )}
        </button>
      </div>

      <div className="security-notice">
        <FontAwesomeIcon icon={faLock} />
        <span>Your payment information is secure and encrypted</span>
      </div>
    </form>
  );
};

const DashboardUnlock = ({ isOpen, onClose, estimatedSavings, onSuccess, username }) => {
  if (!isOpen) return null;

  const handleSuccess = (paymentIntent, unlockFee, savings) => {
    if (onSuccess) {
      onSuccess(paymentIntent, unlockFee, savings);
    }
  };

  return (
    <div className="dashboard-unlock-overlay" onClick={onClose}>
      <div className="dashboard-unlock-modal" onClick={(e) => e.stopPropagation()}>
        <div className="unlock-header">
          <h2>Unlock Detailed Insights</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="unlock-content">
          <Elements stripe={stripePromise}>
            <UnlockForm
              estimatedSavings={estimatedSavings}
              onSuccess={handleSuccess}
              onCancel={onClose}
              username={username}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

export default DashboardUnlock;
